import {Group, type Color, type Object3D, type Texture} from 'three'

import {
  LEADIN_FADE_SECONDS,
  LEADIN_STEP_COUNT,
  LEADIN_STEP_INTERVAL_SECONDS,
  LEADIN_TOTAL_SECONDS,
} from '../config'
import type {PlacementConfig} from '../content/manifest.types'
import {createFootprintTrail, type FootprintTrail, type FootprintTrailDebug} from '../effects/footprint-trail'
import {pawPrintColor} from '../effects/paw-mask'

/**
 * The gap between the tap and the character: paw prints land, then it arrives.
 *
 * This file owns the lead-in's beat sheet and, at the end of it, the character's
 * cue. `entrance-effect.ts` owns the landing's beat sheet the same way, and the
 * two stay separate on purpose: the landing effect is a LOOK, and this is a
 * piece of the app's sequencing. Folding the cue into `src/effects/` would make
 * the thing documented as never touching the model the thing that starts it, and
 * would have to work even for `effect: 'none'`, where that module is a no-op by
 * design.
 *
 * LIVES IN src/placement/ AND NOT src/effects/. `PlacementConfig`'s own doc
 * says it is "about the instant the user taps the floor - which is also what
 * src/placement/ is named after". This is that instant, stretched out.
 *
 * WHAT IT DOES NOT OWN: the character's visibility. `PlacementController.place`
 * hides the content and `reveal()` shows it, and this module only decides WHEN
 * by calling `onReveal`. Handing it a reference to the model would grow exactly
 * the effects-to-model dependency the entrance effect is careful not to have.
 *
 * PARENTING: `root` belongs under `PlacementController.root`, added AFTER
 * `setContent()` - that calls `root.clear()` and would take this with it. Living
 * under the placement root is what gives the trail the tap's position and the
 * yaw that aims it at the camera.
 */

export type SequenceWarning = {readonly kind: 'noIntroForLeadIn'}

export interface PlacementSequenceDebug {
  readonly leadIn: PlacementConfig['leadIn']
  readonly running: boolean
  readonly revealed: boolean
  readonly isLeadingIn: boolean
  readonly elapsedSeconds: number
  readonly durationSeconds: number
  readonly trail: FootprintTrailDebug | null
}

export interface PlacementSequence {
  /** Add this under `PlacementController.root`, after `setContent()`. */
  readonly root: Object3D
  /**
   * Runs the lead-in from the top. Always a hard restart - a tap during one is
   * the same gesture as the tap that began it, and every other part of a
   * placement restarts on it too.
   *
   * With no lead-in configured this calls `onReveal` synchronously and returns,
   * so the caller never has to ask whether there is one.
   */
  start(): void
  update(deltaSeconds: number): void
  /**
   * True while the character is being held back.
   *
   * Read by `onPointerDown` to keep taps falling through to placement: the model
   * tap proxy is still standing where the character is GOING to be, and
   * `Raycaster` does not check `visible`, so without this a tap on the trail
   * would be swallowed by a reaction nobody can see.
   */
  readonly isLeadingIn: boolean
  readonly debug: PlacementSequenceDebug
  readonly warnings: readonly SequenceWarning[]
  dispose(): void
}

export interface PlacementSequenceDeps {
  readonly config: PlacementConfig
  /** Handed to the trail. Borrowed - built and disposed by app-module. */
  readonly mask: Texture
  /**
   * Already resolved, in the working colour space.
   *
   * Passed in rather than derived here for the same reason `EntranceEffectDeps`
   * takes one: deriving it means reading the model.
   */
  readonly accentColor: Color
  /** `NormalizedModel.groundRadius`. Sizes the stride and the prints. */
  readonly groundRadius: number
  /**
   * `animation.introClipName !== null`.
   *
   * A lead-in is a promise that something is arriving. Without an entrance clip
   * the character simply appears at the end of it, breaking that promise - so
   * this is warned about rather than papered over with an invented scale-up. No
   * clip, no behaviour, no guessing, the same posture `animation.ts` takes.
   */
  readonly hasIntro: boolean
  /**
   * Called exactly once per `start()`, at the end of the lead-in.
   *
   * CONTRACT: must not call back into this object. With `leadIn: 'none'` it is
   * invoked synchronously from `start()`, so a call back would re-enter.
   */
  readonly onReveal: () => void
  /**
   * Called once per paw print, `index` counting from 0, on the frame that print
   * is born. Index 0 fires synchronously from `start()`, for the same reason
   * `trail.setElapsed(0)` is called there: that print lands on the tap's own
   * frame, and a cue that waits for the next update is a cue out of phase with
   * the thing it is announcing.
   *
   * Never fires with `leadIn: 'none'` - there are no prints to announce.
   *
   * CONTRACT: must not call back into this object, same as `onReveal`.
   */
  readonly onStep?: (index: number) => void
}

export const describeSequenceWarning = (warning: SequenceWarning): string => {
  switch (warning.kind) {
    case 'noIntroForLeadIn':
      return "placement.leadIn is set but the model has no intro clip; the character will pop in when the lead-in ends"
  }
}

export const createPlacementSequence = ({
  config,
  mask,
  accentColor,
  groundRadius,
  hasIntro,
  onReveal,
  onStep,
}: PlacementSequenceDeps): PlacementSequence => {
  const warnings: SequenceWarning[] = []

  const baseDebug = {
    leadIn: config.leadIn,
    durationSeconds: config.leadIn === 'none' ? 0 : LEADIN_TOTAL_SECONDS,
  } as const

  // Mirrors createEntranceEffect's no-effect early return: an opted-out campaign
  // gets a real object with the same shape, and `start` IS the reveal, so the
  // placement beat lands in the same JS turn as the tap and renders bit-for-bit
  // as it did before this file existed.
  if (config.leadIn === 'none') {
    const empty = new Group()
    empty.name = 'placement-sequence-disabled'
    return {
      root: empty,
      start: onReveal,
      update: () => {},
      isLeadingIn: false,
      debug: {
        ...baseDebug,
        running: false,
        revealed: false,
        isLeadingIn: false,
        elapsedSeconds: 0,
        trail: null,
      },
      warnings,
      dispose: () => {},
    }
  }

  if (!hasIntro) warnings.push({kind: 'noIntroForLeadIn'})

  const trail: FootprintTrail = createFootprintTrail({
    mask,
    // Decided out here rather than inside the trail, the way the ring's colour
    // is decided in entrance-effect.ts.
    color: pawPrintColor(accentColor),
    groundRadius,
  })

  const group = new Group()
  group.name = 'placement-sequence'
  group.visible = false
  group.add(trail.object)

  let elapsed = 0
  let running = false
  let revealed = false
  let stepsFired = 0

  return {
    root: group,

    start: () => {
      elapsed = 0
      running = true
      revealed = false
      group.visible = true
      // Frame 0 applied NOW, not on the next update. The first print has to land
      // on the tap's own frame: on a hand-held phone, 100 ms with no visible
      // answer reads as "it did not register", and the next thing that happens
      // is another tap that restarts this.
      trail.setElapsed(0)
      // With it, and for the same reason. `update` picks up from index 1.
      stepsFired = 1
      onStep?.(0)
    },

    update: (deltaSeconds) => {
      if (!running) return
      elapsed += deltaSeconds
      trail.setElapsed(elapsed)

      // Deliberately the same expression as `FootprintTrail.debug.bornCount`
      // rather than a running countdown of its own. The births live in a vertex
      // attribute and are evaluated on the GPU, so nothing can be observed from
      // here - which means the only way a cue and its print stay together
      // through a change to the cadence is if both are computed from it.
      const born = Math.min(
        LEADIN_STEP_COUNT,
        Math.floor(elapsed / LEADIN_STEP_INTERVAL_SECONDS) + 1
      )
      while (stepsFired < born) onStep?.(stepsFired++)

      if (!revealed && elapsed >= LEADIN_TOTAL_SECONDS) {
        revealed = true
        onReveal()
      }
      if (elapsed >= LEADIN_TOTAL_SECONDS + LEADIN_FADE_SECONDS) {
        running = false
        // Idle costs nothing: no draw calls until the next placement.
        group.visible = false
      }
    },

    get isLeadingIn() {
      return running && !revealed
    },

    get debug() {
      return {
        ...baseDebug,
        running,
        revealed,
        isLeadingIn: running && !revealed,
        elapsedSeconds: elapsed,
        trail: trail.debug,
      }
    },

    warnings,

    dispose: () => {
      trail.dispose()
      group.removeFromParent()
    },
  }
}
